import React, { useEffect } from 'react'
import { useParams } from 'react-router'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { signal } from '@preact-signals/safe-react'
import { useCookies } from 'react-cookie'
import { Footer, Navigation } from './components'
import { useTranslation } from 'react-i18next'

import { activeCountries } from './constant/countries'

import logo from './images/loop-logo.svg'

import { refetchUser } from './pages/login/Login'

export const user = {
  balance: signal(null),
  bankData: signal(null),
  isLoading: signal(false),
  isPageLoading: signal(false),
  me: signal(null),
  refreshToken: signal(null),
  token: signal(null),
  withdraw: signal(null),
}

const trackingId = 'G-H93B0TK575'

const oneTrustKey = import.meta.env.VITE_ONETRUST_KEY || ''

const Layout = () => {
  const { country } = useParams()
  const navigate = useNavigate()
  const { i18n } = useTranslation()

  useEffect(() => {
    // TODO Remove when locale params not necessary anymore
    const localeFromUrl = new URLSearchParams(window.location.search).has('locale')

    if (localeFromUrl) return

    if (country && Object.keys(activeCountries).includes(country.toLowerCase())) {
      const activeLocale = activeCountries[country.toLowerCase()].locales[0]

      localStorage.setItem('new_locale', activeLocale)
      i18n.changeLanguage(activeLocale)

      window?.OneTrust?.changeLanguage(activeLocale.split('-')[0])

      if (localStorage.getItem('redirect')) {
        navigate(-2)
      } else {
        navigate(window.location.pathname.replace(`/${country}`, '') || '/', { replace: true })
      }

      localStorage.removeItem('redirect')
    }

    const hasStoredLocale = !!(
      new URLSearchParams(window.location.search).get('locale') || localStorage.getItem('new_locale')
    )

    if (!hasStoredLocale) {
      localStorage.setItem('redirect', true)
      navigate('/countries')
    }
  }, [country])

  const [cookies, , removeCookie] = useCookies(['token', 'refreshToken'])
  const location = useLocation()
  const isHeadless = new URLSearchParams(location.search).has('headless')

  if (!user.me.value && cookies.token) {
    refetchUser(cookies.token, removeCookie)
  }

  if (!user.me.value && user.isLoading.value) return false

  return (
    <>
      <title>Loop Mobile WebApp</title>

      <Helmet>
        {!isHeadless && !window?.OneTrust && (
          <script
            src={`https://cdn.cookielaw.org/consent/${oneTrustKey}/OtAutoBlock.js`}
            type="text/javascript"
          >
          </script>
        )}

        {!isHeadless && !window?.OneTrust && (
          <script
            data-domain-script={oneTrustKey}
            data-language={i18n.language.split('-')[0]}
            src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
            type="text/javascript"
          >
          </script>
        )}

        <script async src={`https://www.googletagmanager.com/gtag/js?id=${trackingId}`}></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){window.dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${trackingId}');
          `}
        </script>
      </Helmet>

      <meta content="Manage your balance and request withdrawals" name="description" />
      <meta content="loop, recycling, reusable, sustainable" name="keywords" />
      <meta content="TerraCycle - Loop" name="author" />
      <meta content="Loop Mobile WebApp" property="og:title" />
      <meta content="Manage your balance and request withdrawals" property="og:description" />
      <meta content={logo} property="og:image" />
      <meta content={logo} name="twitter:image" />
      <meta content="Loop Mobile WebApp" name="twitter:title" />
      <meta content="Manage your balance and request withdrawals" name="twitter:description" />

      <div className="container mx-auto">
        <div className="row">
          <div className="col-12">
            <div className="logo" />
            {!isHeadless && <Navigation />}

            <div className="row">
              <div className="col-12 mb-150">
                <Outlet />
              </div>
            </div>

            {!isHeadless && <Footer />}
          </div>
        </div>
      </div>
    </>
  )
}

export default Layout
